import { FormEvent, useState } from "react";
import { PasswordField } from "./password-field.js";

type RecoveryResult = { data: { reset: boolean } };
type RecoveryRequestResult = { data: { requested: boolean; deliveryAvailable?: boolean } };

export function PasswordRecovery({ token }: { token: string | null }) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [completed, setCompleted] = useState(false);
  const [email, setEmail] = useState("");

  async function requestRecovery(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage("");
    setBusy(true);
    try {
      const response = await fetch("/api/v1/public/auth/password-recovery/request", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const body = (await response.json().catch(() => ({}))) as { message?: string } & RecoveryRequestResult;
      if (!response.ok) throw new Error(body.message ?? "Não foi possível solicitar a recuperação.");
      if (!body.data?.requested) throw new Error("Não foi possível solicitar a recuperação.");
      setMessage(body.data.deliveryAvailable === false
        ? "A recuperação por e-mail ainda não está disponível neste ambiente. Fale com a equipe responsável pelo seu acesso."
        : "Se houver uma conta elegível para este e-mail, enviaremos um link seguro para redefinir a senha.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível solicitar a recuperação.");
    } finally {
      setBusy(false);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage("");
    if (!token) {
      setMessage("Este link de recuperação está incompleto.");
      return;
    }
    if (password !== confirmation) {
      setMessage("As senhas não conferem.");
      return;
    }
    setBusy(true);
    try {
      const response = await fetch("/api/v1/public/auth/password-recovery/complete", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const body = (await response.json().catch(() => ({}))) as {
        message?: string;
      };
      if (!response.ok)
        throw new Error(body.message ?? "Não foi possível redefinir a senha.");
      const result = body as RecoveryResult;
      if (!result.data?.reset) throw new Error("Não foi possível concluir a redefinição.");
      window.history.replaceState({}, "", "/recuperar-acesso");
      setPassword("");
      setConfirmation("");
      setCompleted(true);
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Não foi possível redefinir a senha.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="recovery-shell">
      <section className="recovery-card">
        <a className="recovery-brand" href="/">
          <span>G3</span> G3.AI
        </a>
        {!token ? (
          <>
            <p className="recovery-kicker">RECUPERAÇÃO SEGURA</p>
            <h1>Esqueceu sua senha?</h1>
            <p>Informe o e-mail usado no seu acesso. Se houver uma conta elegível, enviaremos um link seguro de uso único.</p>
            <form onSubmit={requestRecovery}>
              <label>
                E-mail da sua conta
                <input type="email" autoComplete="email" maxLength={254} value={email} onChange={(event) => setEmail(event.target.value)} required />
              </label>
              {message && <p className="recovery-error" role="status">{message}</p>}
              <button className="recovery-primary" disabled={busy}>{busy ? "Enviando…" : "Enviar link de recuperação"}</button>
            </form>
            <p><a href="/app">Voltar para entrar</a></p>
          </>
        ) : completed ? (
          <>
            <p className="recovery-kicker">ACESSO RESTABELECIDO</p>
            <h1>Nova senha definida.</h1>
            <p>
              Por segurança, os acessos anteriores foram encerrados. Entre novamente
              para continuar na Clínica Íris Estética.
            </p>
            <a className="recovery-primary" href="/app">
              Entrar no painel
            </a>
          </>
        ) : (
          <>
            <p className="recovery-kicker">RECUPERAÇÃO SEGURA</p>
            <h1>Defina uma nova senha.</h1>
            <p>
              Este link é pessoal, de uso único e expira em poucos minutos. Escolha uma
              senha exclusiva para este acesso.
            </p>
            <form onSubmit={submit}>
              <label>
                Nova senha
                <PasswordField
                  autoComplete="new-password"
                  minLength={12}
                  maxLength={128}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </label>
              <label>
                Confirmar nova senha
                <PasswordField
                  autoComplete="new-password"
                  minLength={12}
                  maxLength={128}
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  required
                />
              </label>
              {message && <p className="recovery-error" role="alert">{message}</p>}
              <button className="recovery-primary" disabled={busy}>
                {busy ? "Definindo senha…" : "Definir nova senha"}
              </button>
            </form>
          </>
        )}
      </section>
    </main>
  );
}
